import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Shield, Headphones, CheckCircle, ArrowRight,
  Phone, Clock, Users, Activity, Award, Zap, Lock,
  ChevronRight, MessageCircle, Tag, Loader2, XCircle
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brand colors
const BRAND = {
  primary: "#1e3a6e",    // Navy blue (from logo)
  secondary: "#2952a3",  // Medium blue
  accent: "#3b6dd6",     // Bright blue
  light: "#e8eef8",      // Light blue bg
  lighter: "#f0f4fa",    // Very light blue
};

export default function LandingPage() {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  // Registration form
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", coupon_code: "" });
  const [couponStatus, setCouponStatus] = useState(null);
  const [couponDetails, setCouponDetails] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [regError, setRegError] = useState("");
  const [sessionData, setSessionData] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const validateCoupon = async () => {
    if (!formData.coupon_code.trim()) return;
    setCouponStatus("checking");
    try {
      const res = await axios.post(`${API}/public/hearing-test/validate-coupon?code=${formData.coupon_code}`);
      setCouponStatus("valid");
      setCouponDetails(res.data);
    } catch {
      setCouponStatus("invalid");
      setCouponDetails(null);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) {
      setRegError("Please enter your name and phone number");
      return;
    }
    if (formData.phone.replace(/\D/g, "").length < 10) {
      setRegError("Please enter a valid 10-digit phone number");
      return;
    }
    setRegistering(true);
    setRegError("");

    // Auto-validate coupon if typed but not yet applied
    let activeCoupon = couponDetails;
    if (formData.coupon_code.trim() && couponStatus !== "valid") {
      try {
        const cRes = await axios.post(`${API}/public/hearing-test/validate-coupon?code=${formData.coupon_code}`);
        setCouponStatus("valid");
        setCouponDetails(cRes.data);
        activeCoupon = cRes.data;
      } catch {
        // Invalid coupon — continue without it but warn
        setCouponStatus("invalid");
      }
    }

    try {
      const res = await axios.post(`${API}/public/hearing-test/register`, formData);
      setSessionData(res.data);
      if (res.data.payment_required) {
        initiatePayment(res.data);
      } else {
        navigate(`/pta-test/patient/${res.data.session_code}?autostart=true`);
      }
    } catch (err) {
      setRegError(err.response?.data?.detail || "Registration failed. Please try again.");
      setRegistering(false);
    }
  };

  const initiatePayment = async (session) => {
    try {
      const res = await axios.post(`${API}/public/hearing-test/${session.session_id}/create-payment`);
      if (res.data.already_paid) {
        navigate(`/pta-test/patient/${session.session_code}?autostart=true`);
        return;
      }
      const options = {
        key: res.data.key_id,
        amount: res.data.amount,
        currency: "INR",
        name: "HearClear",
        description: "AI Hearing Assessment",
        image: "/images/hearclear_logo.png",
        order_id: res.data.order_id,
        handler: async (response) => {
          try {
            await axios.post(
              `${API}/public/hearing-test/${session.session_id}/verify-payment?payment_id=${response.razorpay_payment_id}&order_id=${response.razorpay_order_id}&signature=${response.razorpay_signature}`
            );
            navigate(`/pta-test/patient/${session.session_code}?autostart=true`);
          } catch {
            setRegError("Payment verification failed. Please contact support.");
            setRegistering(false);
          }
        },
        prefill: { name: session.patient_name, contact: formData.phone, email: formData.email },
        theme: { color: BRAND.primary },
        modal: { ondismiss: () => { setRegistering(false); } }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setRegError("Could not initiate payment. Please try again.");
      setRegistering(false);
    }
  };

  const finalPrice = couponDetails ? couponDetails.final_price : 49900;
  const displayPrice = (finalPrice / 100).toFixed(0);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-white"}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <a href="https://hearclear.in" data-testid="logo-link" className="flex items-center gap-2">
            <img src="/images/hearclear_logo.png" alt="HearClear" className="h-10 w-auto" data-testid="header-logo" />
          </a>
          <div className="flex items-center gap-3">
            <a href="#how-it-works" className="hidden sm:block text-sm text-gray-600 hover:text-blue-700 transition-colors">How It Works</a>
            <a href="/my-tests" data-testid="nav-my-tests" className="text-sm font-medium text-blue-800 hover:text-blue-900 transition-colors">My Tests</a>
            <a href="/login" data-testid="nav-staff-login" className="text-xs text-gray-400 hover:text-gray-600 transition-colors">Staff</a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 pb-12 sm:pt-28 sm:pb-16 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: `linear-gradient(to bottom, ${BRAND.light}70, white)` }} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-1.5 mb-5">
                <Activity className="w-4 h-4 text-blue-700" />
                <span className="text-sm font-medium text-blue-800">AI-Powered Clinical Grade Test</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-4" data-testid="hero-heading">
                Get Your Hearing<br/>Tested by <span style={{ color: BRAND.primary }}>AI</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-600 mb-4 max-w-lg leading-relaxed">
                India's most trusted AI-driven hearing test. Take it from anywhere, anytime.
                Get instant clinical-grade results and personalized hearing aid recommendations.
              </p>
              <p className="text-sm text-blue-800 font-medium mb-6">All You Hear, Crystal Clear.</p>
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-700"><Shield className="w-4 h-4" style={{ color: BRAND.secondary }} /> Clinically Calibrated</div>
                <div className="flex items-center gap-2 text-sm text-gray-700"><Clock className="w-4 h-4" style={{ color: BRAND.secondary }} /> 8-10 Minutes</div>
                <div className="flex items-center gap-2 text-sm text-gray-700"><Lock className="w-4 h-4" style={{ color: BRAND.secondary }} /> 100% Private</div>
              </div>
              <Button data-testid="hero-cta" onClick={scrollToForm} size="lg"
                className="text-white rounded-full px-8 h-12 text-base font-semibold shadow-lg"
                style={{ background: BRAND.primary, boxShadow: `0 8px 24px ${BRAND.primary}30` }}>
                Take the Test Now <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="hidden lg:block">
              <div className="rounded-3xl p-8 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.accent})` }}>
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-10 -mt-10" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-8 -mb-8" />
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                      <Activity className="w-7 h-7" />
                    </div>
                    <div>
                      <p className="font-bold text-xl">Clinical Standard</p>
                      <p className="text-blue-100 text-sm">Gold Standard Protocol</p>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm">
                    {[
                      "Tests 6 frequencies per ear (250Hz - 8kHz)",
                      "AI detects your exact hearing thresholds",
                      "Randomized timing prevents false responses",
                      "Instant PDF report + AI recommendations",
                    ].map((t, i) => (
                      <div key={i} className="flex items-center gap-3 bg-white/10 rounded-xl p-3">
                        <CheckCircle className="w-5 h-5 text-blue-200 shrink-0" />
                        <span>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-8 border-y border-gray-100" style={{ background: BRAND.lighter }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { num: "50,000+", label: "Happy Customers", icon: Users },
              { num: "98%", label: "Accuracy Rate", icon: Award },
              { num: "40+", label: "Clinic Partners", icon: Activity },
              { num: "8 Min", label: "Average Test Time", icon: Clock },
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center">
                <s.icon className="w-5 h-5 mb-1" style={{ color: BRAND.secondary }} />
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: BRAND.primary }}>{s.num}</p>
                <p className="text-xs sm:text-sm text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">How It Works</h2>
            <p className="text-gray-600 max-w-md mx-auto">Four simple steps to know your hearing health</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Register", desc: "Enter your name & phone. Apply coupon if you have one.", icon: Users, color: "bg-blue-50 text-blue-700 border-blue-200" },
              { step: "2", title: "Pay Securely", desc: "Quick payment via Razorpay. 100% secure & encrypted.", icon: Lock, color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
              { step: "3", title: "Take the Test", desc: "Wear headphones. Press the button when you hear a tone. AI handles everything.", icon: Headphones, color: "bg-sky-50 text-sky-700 border-sky-200" },
              { step: "4", title: "Get Results", desc: "Instant PDF report + AI hearing aid recommendations sent to your WhatsApp.", icon: Activity, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
            ].map((s, i) => (
              <Card key={i} className="border border-gray-100 hover:shadow-md transition-shadow" data-testid={`step-card-${s.step}`}>
                <CardContent className="p-6">
                  <div className={`w-10 h-10 rounded-xl ${s.color} border flex items-center justify-center mb-4`}>
                    <s.icon className="w-5 h-5" />
                  </div>
                  <div className="text-xs font-bold text-gray-400 mb-1">STEP {s.step}</div>
                  <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Registration + Pricing */}
      <section id="register" className="py-14 sm:py-20" style={{ background: `linear-gradient(to bottom, ${BRAND.lighter}, white)` }} ref={formRef}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            {/* Pricing Card */}
            <div>
              <div className="text-center lg:text-left mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Start Your Hearing Test</h2>
                <p className="text-gray-600">Register below and take the test in under 10 minutes</p>
              </div>
              <Card className="border-2 border-blue-200 bg-white shadow-xl overflow-hidden" data-testid="pricing-card">
                <div className="px-6 py-4 text-white text-center" style={{ background: BRAND.primary }}>
                  <p className="text-sm font-medium text-blue-200 mb-1">AI-Powered Hearing Assessment</p>
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-2xl text-blue-300 line-through font-medium">&#8377;999</span>
                    <span className="text-4xl font-bold">&#8377;{couponDetails?.final_price === 0 ? "FREE" : displayPrice}</span>
                  </div>
                  {couponDetails && couponStatus === "valid" && (
                    <Badge className="mt-2 bg-white/20 text-white border-white/30">{couponDetails.discount_text} Applied</Badge>
                  )}
                </div>
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    {[
                      "Full Pure Tone Audiometry (both ears)",
                      "AI-driven clinical standard protocol",
                      "Calibrated across 6 key frequencies",
                      "Instant PDF report with audiogram",
                      "AI hearing aid recommendations",
                      "Report delivered to your WhatsApp",
                      "Login anytime to view past results",
                      "Recommendations linked to HearClear shop",
                    ].map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: BRAND.secondary }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Registration Form */}
            <Card className="shadow-xl border-gray-200" data-testid="registration-form">
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-gray-700">Full Name *</Label>
                    <Input data-testid="reg-name" id="name" placeholder="Enter your full name" value={formData.name}
                      onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} className="mt-1.5 h-11" required />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Phone Number *</Label>
                    <div className="flex mt-1.5">
                      <div className="flex items-center px-3 bg-gray-100 border border-r-0 border-gray-200 rounded-l-md text-sm text-gray-500">+91</div>
                      <Input data-testid="reg-phone" id="phone" placeholder="98765 43210" value={formData.phone}
                        onChange={e => setFormData(p => ({ ...p, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                        className="h-11 rounded-l-none" required />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email (Optional)</Label>
                    <Input data-testid="reg-email" id="email" type="email" placeholder="you@example.com" value={formData.email}
                      onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} className="mt-1.5 h-11" />
                  </div>
                  <div>
                    <Label htmlFor="coupon" className="text-sm font-medium text-gray-700">
                      <Tag className="w-3.5 h-3.5 inline mr-1" /> Coupon Code
                    </Label>
                    <div className="flex gap-2 mt-1.5">
                      <Input data-testid="reg-coupon" id="coupon" placeholder="Enter code" value={formData.coupon_code}
                        onChange={e => { setFormData(p => ({ ...p, coupon_code: e.target.value })); setCouponStatus(null); setCouponDetails(null); }}
                        className="h-11 uppercase" />
                      <Button type="button" variant="outline" data-testid="apply-coupon-btn" onClick={validateCoupon}
                        disabled={!formData.coupon_code.trim() || couponStatus === "checking"} className="h-11 px-4 shrink-0">
                        {couponStatus === "checking" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                      </Button>
                    </div>
                    {couponStatus === "valid" && (
                      <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5" /> {couponDetails.discount_text} — {couponDetails.description}
                      </p>
                    )}
                    {couponStatus === "invalid" && (
                      <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                        <XCircle className="w-3.5 h-3.5" /> Invalid or expired coupon
                      </p>
                    )}
                  </div>

                  {regError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="reg-error">{regError}</div>
                  )}

                  <Button data-testid="register-btn" type="submit" disabled={registering}
                    className="w-full h-12 text-white text-base font-semibold rounded-xl shadow-lg mt-2"
                    style={{ background: BRAND.primary, boxShadow: `0 8px 24px ${BRAND.primary}30` }}>
                    {registering ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
                    ) : (
                      <>{finalPrice === 0 ? "Start Free Test" : `Pay \u20B9${displayPrice} & Start Test`} <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>

                  <p className="text-xs text-gray-400 text-center mt-3">
                    By registering, you agree to our terms. Your data is 100% secure and encrypted.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Trust */}
      <section className="py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Why Trust HearClear?</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: "Clinically Validated", desc: "Uses the same clinical protocol used in audiology clinics worldwide. Tested and trusted." },
              { icon: Activity, title: "AI-Powered Accuracy", desc: "AI adapts tone levels in real-time, detecting your exact hearing thresholds with precision." },
              { icon: Award, title: "Calibrated Frequencies", desc: "International audiology standard frequency corrections for accurate results." },
              { icon: Zap, title: "Instant Results", desc: "Get your audiogram, severity classification, and PDF report immediately after the test." },
              { icon: Users, title: "50,000+ Customers", desc: "India's largest hearing care network with 40+ clinics across 18 cities." },
              { icon: Lock, title: "Private & Secure", desc: "Your health data is encrypted and never shared without your consent." },
            ].map((f, i) => (
              <div key={i} className="flex gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: BRAND.light }}>
                  <f.icon className="w-5 h-5" style={{ color: BRAND.secondary }} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{f.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 sm:py-20" style={{ background: BRAND.lighter }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: "What equipment do I need?", a: "Just a smartphone or laptop with wired headphones (not Bluetooth). Wired headphones give the most accurate results." },
              { q: "How accurate is the online test?", a: "Our AI uses a clinical standard protocol with calibrated frequencies — the same standard used in professional audiology clinics. Results are clinically meaningful." },
              { q: "How long does the test take?", a: "About 8-10 minutes. The AI tests 6 frequencies in each ear automatically." },
              { q: "What happens after the test?", a: "You get an instant PDF report with your audiogram, hearing classification, and AI-powered recommendations. The report is also sent to your WhatsApp." },
              { q: "Can I retake the test?", a: "Yes! You can take the test as many times as you want. Each test is stored in your account accessible via OTP login." },
              { q: "Is my data secure?", a: "Absolutely. All data is encrypted and stored securely. We never share your health information without consent." },
            ].map((faq, i) => (
              <details key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden group">
                <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                  <span className="font-medium text-gray-900 text-sm">{faq.q}</span>
                  <ChevronRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90" />
                </summary>
                <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 sm:py-20 text-white" style={{ background: BRAND.primary }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Don't Wait. Test Your Hearing Today.</h2>
          <p className="text-blue-200 mb-8 max-w-lg mx-auto">Early detection is key. Get clinical-grade results in just 10 minutes from the comfort of your home.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={scrollToForm} size="lg" className="bg-white hover:bg-blue-50 rounded-full px-8 h-12 font-semibold shadow-lg" style={{ color: BRAND.primary }} data-testid="final-cta">
              Take the Test — &#8377;499 <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 h-12" data-testid="call-cta">
              <a href="tel:+919888438843"><Phone className="w-4 h-4 mr-2" /> Call Us: +91 98884 38843</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-900 text-gray-400">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/images/hearclear_logo.png" alt="HearClear" className="h-8 w-auto brightness-200" />
              <span className="text-xs text-gray-500">All You Hear, Crystal Clear</span>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <a href="https://hearclear.in" className="hover:text-white transition-colors">hearclear.in</a>
              <a href="https://hearclear.in/products" className="hover:text-white transition-colors">Shop</a>
              <a href="/my-tests" className="hover:text-white transition-colors">My Tests</a>
              <a href="tel:+919888438843" className="hover:text-white transition-colors">+91 98884 38843</a>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating */}
      <a href="https://wa.me/919971925446?text=Hi!%20I%20want%20to%20take%20a%20hearing%20test" target="_blank" rel="noreferrer"
        className="fixed bottom-5 right-5 z-50 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 transition-colors" data-testid="whatsapp-btn">
        <MessageCircle className="w-6 h-6 text-white" />
      </a>
    </div>
  );
}
